import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parse,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import {
  Ban,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  Plus,
  UserX,
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import {
  Appointment,
  APPOINTMENT_TYPE_COLORS,
  APPOINTMENT_TYPE_LABELS,
  AppointmentType,
  BLOCK_TYPE_LABELS,
} from '../types/appointment';
import { NewAppointmentModal } from '../components/NewAppointmentModal';
import { BlockOutModal } from '../components/BlockOutModal';
import { AppointmentDetailModal } from '../components/AppointmentDetailModal';

type CalendarView = 'day' | 'week' | 'month';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 7); // 07:00 - 18:00

export function AppointmentsPage() {
  const { get, post, patch, del } = useApi();
  const [view, setView] = useState<CalendarView>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modals
  const [showNewAppt, setShowNewAppt] = useState(false);
  const [showBlockOut, setShowBlockOut] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState<Appointment | null>(null);
  const [prefillDate, setPrefillDate] = useState('');
  const [prefillTime, setPrefillTime] = useState('');

  const dateRange = useMemo(() => {
    if (view === 'day') {
      return { start: format(currentDate, 'yyyy-MM-dd'), end: format(currentDate, 'yyyy-MM-dd') };
    }
    if (view === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return { start: format(weekStart, 'yyyy-MM-dd'), end: format(weekEnd, 'yyyy-MM-dd') };
    }
    // month
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    return { start: format(monthStart, 'yyyy-MM-dd'), end: format(monthEnd, 'yyyy-MM-dd') };
  }, [view, currentDate]);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await get<Appointment[]>(
        `/appointments?startDate=${dateRange.start}&endDate=${dateRange.end}`
      );
      if (result.success && result.data) {
        setAppointments(result.data);
      } else {
        setError(result.error || 'Failed to load');
      }
    } catch {
      setError('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [get, dateRange]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const navigate = useCallback(
    (dir: 'prev' | 'next' | 'today') => {
      if (dir === 'today') {
        setCurrentDate(new Date());
        return;
      }
      const delta = dir === 'next' ? 1 : -1;
      if (view === 'day') setCurrentDate(d => addDays(d, delta));
      else if (view === 'week') setCurrentDate(d => (delta > 0 ? addWeeks(d, 1) : subWeeks(d, 1)));
      else setCurrentDate(d => (delta > 0 ? addMonths(d, 1) : subMonths(d, 1)));
    },
    [view]
  );

  const handleSlotClick = useCallback((date: string, time: string) => {
    setPrefillDate(date);
    setPrefillTime(time);
    setShowNewAppt(true);
  }, []);

  const handleCreated = useCallback(() => {
    setShowNewAppt(false);
    setShowBlockOut(false);
    fetchAppointments();
  }, [fetchAppointments]);

  const handleUpdated = useCallback(() => {
    setSelectedAppt(null);
    fetchAppointments();
  }, [fetchAppointments]);

  const headerLabel = useMemo(() => {
    if (view === 'day') return format(currentDate, 'EEEE, d MMMM yyyy');
    if (view === 'week') {
      const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
      const we = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(ws, 'd MMM')} – ${format(we, 'd MMM yyyy')}`;
    }
    return format(currentDate, 'MMMM yyyy');
  }, [view, currentDate]);

  // Week view: days
  const weekDays = useMemo(() => {
    const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  }, [currentDate]);

  return (
    <main className="max-w-full mx-auto px-4 py-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-xl font-bold text-gray-900 mr-1">{headerLabel}</h1>
        <div className="flex items-center gap-1">
          <button onClick={() => navigate('prev')} className="p-1.5 rounded hover:bg-gray-100">
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <button
            onClick={() => navigate('today')}
            className="px-3 py-1 text-sm font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
          >
            Today
          </button>
          <button onClick={() => navigate('next')} className="p-1.5 rounded hover:bg-gray-100">
            <ChevronRight className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        <div className="flex items-center gap-3 ml-auto">
          {/* View switcher */}
          <div className="flex border border-gray-300 rounded-md overflow-hidden">
            {(['day', 'week', 'month'] as CalendarView[]).map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-sm font-medium capitalize ${
                  view === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {v}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowBlockOut(true)}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1.5"
          >
            <Ban className="h-4 w-4" /> Block Out
          </button>
          <button
            onClick={() => { setPrefillDate(format(currentDate, 'yyyy-MM-dd')); setPrefillTime('09:00'); setShowNewAppt(true); }}
            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1.5"
          >
            <Plus className="h-4 w-4" /> New Appointment
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
      )}

      {!loading && view === 'month' && (
        <MonthView
          currentDate={currentDate}
          appointments={appointments}
          onDayClick={d => { setCurrentDate(d); setView('day'); }}
          onApptClick={setSelectedAppt}
        />
      )}

      {!loading && (view === 'week' || view === 'day') && (
        <TimeGridView
          days={view === 'day' ? [currentDate] : weekDays}
          appointments={appointments}
          onSlotClick={handleSlotClick}
          onApptClick={setSelectedAppt}
        />
      )}

      {/* Modals */}
      {showNewAppt && (
        <NewAppointmentModal
          defaultDate={prefillDate}
          defaultTime={prefillTime}
          onClose={() => setShowNewAppt(false)}
          onCreated={handleCreated}
        />
      )}
      {showBlockOut && (
        <BlockOutModal
          defaultDate={format(currentDate, 'yyyy-MM-dd')}
          onClose={() => setShowBlockOut(false)}
          onCreated={handleCreated}
        />
      )}
      {selectedAppt && (
        <AppointmentDetailModal
          appointment={selectedAppt}
          onClose={() => setSelectedAppt(null)}
          onUpdated={handleUpdated}
        />
      )}
    </main>
  );
}


// --- Time Grid (Day / Week) ---

interface TimeGridProps {
  days: Date[];
  appointments: Appointment[];
  onSlotClick: (date: string, time: string) => void;
  onApptClick: (appt: Appointment) => void;
}

function TimeGridView({ days, appointments, onSlotClick, onApptClick }: TimeGridProps) {
  const isWeek = days.length > 1;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
      <div className="grid" style={{ gridTemplateColumns: `60px repeat(${days.length}, 1fr)` }}>
        {/* Header row */}
        <div className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10" />
        {days.map(day => (
          <div
            key={day.toISOString()}
            className={`sticky top-0 bg-gray-50 border-b border-l border-gray-200 px-2 py-2 text-center z-10 ${
              isSameDay(day, new Date()) ? 'bg-blue-50' : ''
            }`}
          >
            <div className="text-xs text-gray-500 uppercase">{format(day, 'EEE')}</div>
            <div className={`text-lg font-semibold ${isSameDay(day, new Date()) ? 'text-blue-600' : 'text-gray-900'}`}>
              {format(day, 'd')}
            </div>
          </div>
        ))}

        {/* Hour rows */}
        {HOURS.map(hour => (
          <React.Fragment key={hour}>
            <div className="border-b border-gray-100 px-2 py-0 text-right">
              <span className="text-xs text-gray-400 -mt-2 inline-block">{`${hour.toString().padStart(2, '0')}:00`}</span>
            </div>
            {days.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const dayAppts = appointments.filter(
                a => a.date === dateStr && a.status !== 'cancelled'
              );
              // Appointments starting in this hour
              const hourAppts = dayAppts.filter(a => {
                const startHour = parseInt(a.startTime.split(':')[0], 10);
                return startHour === hour;
              });

              return (
                <div
                  key={`${dateStr}-${hour}`}
                  className="border-b border-l border-gray-100 relative h-16 cursor-pointer hover:bg-gray-50"
                  onClick={() => onSlotClick(dateStr, `${hour.toString().padStart(2, '0')}:00`)}
                >
                  {hourAppts.map(appt => (
                    <button
                      key={appt.id}
                      onClick={e => { e.stopPropagation(); onApptClick(appt); }}
                      className={`absolute left-0.5 right-0.5 rounded px-1.5 py-0.5 text-xs text-left overflow-hidden border ${
                        appt.isBlockOut
                          ? 'bg-gray-200 text-gray-700 border-gray-300'
                          : APPOINTMENT_TYPE_COLORS[appt.appointmentType as AppointmentType] || 'bg-blue-100 text-blue-800 border-blue-300'
                      } ${appt.status === 'no_show' ? 'opacity-50' : ''}`}
                      style={{ top: 0, minHeight: '28px' }}
                    >
                      <span className="font-medium truncate block">
                        {appt.isBlockOut
                          ? BLOCK_TYPE_LABELS[appt.blockType as keyof typeof BLOCK_TYPE_LABELS] || 'Block'
                          : appt.patientName || 'Patient'}
                      </span>
                      <span className="text-[10px] opacity-75">
                        {appt.startTime}–{appt.endTime}
                        {!appt.isBlockOut && ` · ${APPOINTMENT_TYPE_LABELS[appt.appointmentType as AppointmentType] || appt.appointmentType}`}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}


// --- Month View ---

interface MonthViewProps {
  currentDate: Date;
  appointments: Appointment[];
  onDayClick: (d: Date) => void;
  onApptClick: (a: Appointment) => void;
}

function MonthView({ currentDate, appointments, onDayClick, onApptClick }: MonthViewProps) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const weeks: Date[][] = [];
  let day = calStart;
  while (day <= calEnd) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(day);
      day = addDays(day, 1);
    }
    weeks.push(week);
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Day headers */}
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
          <div key={d} className="px-2 py-2 text-xs font-medium text-gray-500 text-center uppercase">
            {d}
          </div>
        ))}
      </div>

      {/* Weeks */}
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 border-b border-gray-100 last:border-0">
          {week.map(d => {
            const dateStr = format(d, 'yyyy-MM-dd');
            const dayAppts = appointments.filter(a => a.date === dateStr && a.status !== 'cancelled');
            const isToday = isSameDay(d, new Date());
            const isCurrentMonth = isSameMonth(d, currentDate);

            return (
              <div
                key={dateStr}
                className={`min-h-[80px] p-1 border-l border-gray-100 first:border-l-0 cursor-pointer hover:bg-gray-50 ${
                  !isCurrentMonth ? 'bg-gray-50/50' : ''
                }`}
                onClick={() => onDayClick(d)}
              >
                <div className={`text-sm mb-1 ${isToday ? 'bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center' : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`}>
                  {format(d, 'd')}
                </div>
                {dayAppts.slice(0, 3).map(a => (
                  <button
                    key={a.id}
                    onClick={e => { e.stopPropagation(); onApptClick(a); }}
                    className={`w-full text-left text-[10px] px-1 py-0.5 rounded truncate mb-0.5 ${
                      a.isBlockOut
                        ? 'bg-gray-200 text-gray-600'
                        : APPOINTMENT_TYPE_COLORS[a.appointmentType as AppointmentType] || 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {a.startTime} {a.isBlockOut ? (BLOCK_TYPE_LABELS[a.blockType as keyof typeof BLOCK_TYPE_LABELS] || 'Block') : a.patientName}
                  </button>
                ))}
                {dayAppts.length > 3 && (
                  <div className="text-[10px] text-gray-500 px-1">+{dayAppts.length - 3} more</div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
